import { useState, type CSSProperties, type ReactNode, type ReactElement } from 'react';
import type { ValidationRule } from '@/api/v2Client';
import type { Tier } from '@/lib/validation-rules-grouping';
import { ruleRowCellStyle } from './styles';

// Phase 5.X-validation-rules-tier-grouping —
// Collapsible per-Tier section on /admin/validation-rules. Header
// shows "<Tier> (<count>)" + a chevron; clicking toggles the body
// open/closed. Default expanded so the operator sees the rules
// without an extra click on first paint.
//
// Each section is its own <table> so the column headers re-render
// per group — that matches V1's pattern and keeps Phase 5.13d's
// "headers always visible" invariant on a per-tier basis. A tier the
// filters leave with 0 rules still renders its header (rows 3–5) and,
// expanded, says no rules match instead of showing an empty table.

const sectionStyle: CSSProperties = {
  marginBottom: 16,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
};
// Use only longhand border-* properties (no `border` shorthand) so toggling
// expand/collapse doesn't generate React's "mixing shorthand with non-
// shorthand" warning when borderBottom flips on/off.
const headerBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  background: 'var(--surface2, #f8fafc)',
  borderTopWidth: 0,
  borderRightWidth: 0,
  borderLeftWidth: 0,
  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--border)',
  borderTopLeftRadius: 6,
  borderTopRightRadius: 6,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  cursor: 'pointer',
  userSelect: 'none',
  fontSize: 14,
  fontWeight: 600,
  width: '100%',
  textAlign: 'left',
};
const collapsedHeaderStyle: CSSProperties = {
  ...headerBaseStyle,
  borderBottomWidth: 0,
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
};
const countBadgeStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  padding: '1px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  minWidth: 18,
  textAlign: 'center',
};

export interface TierSectionProps {
  readonly tier: Tier;
  readonly rules: ValidationRule[];
  readonly renderRow: (rule: ValidationRule) => ReactNode;
  /** Defaults to true. Tests can pass false to assert collapsed state. */
  readonly defaultExpanded?: boolean;
}

// Slugify the tier name for stable test IDs. e.g. "ICH Core" → "ich-core".
// Kept module-local (not exported) so this file only exports components —
// otherwise react-refresh's "only-export-components" rule trips.
function tierSlug(tier: string): string {
  return tier.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function TierSection({
  tier,
  rules,
  renderRow,
  defaultExpanded = true,
}: TierSectionProps): ReactElement {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const slug = tierSlug(tier);

  return (
    <section
      data-testid={`tier-section-${slug}`}
      data-tier={tier}
      data-expanded={expanded ? 'true' : 'false'}
      style={sectionStyle}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`tier-body-${slug}`}
        data-testid={`tier-toggle-${slug}`}
        onClick={() => setExpanded((v) => !v)}
        style={expanded ? headerBaseStyle : collapsedHeaderStyle}
      >
        <span aria-hidden="true" style={{ display: 'inline-block', width: 12 }}>
          {expanded ? '▾' : '▸'}
        </span>
        <span style={{ flex: 1 }}>{tier}</span>
        <span
          data-testid={`tier-count-${slug}`}
          style={countBadgeStyle}
          aria-label={`${rules.length} rules`}
        >
          {rules.length}
        </span>
      </button>
      {expanded && rules.length === 0 && (
        // Validation Rules rows 3–5 (item 1) — a tier the filters leave empty
        // keeps its header and says so, instead of an empty table.
        <div
          id={`tier-body-${slug}`}
          data-testid={`tier-empty-${slug}`}
          style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}
        >
          No rules in this tier match the current filters.
        </div>
      )}
      {expanded && rules.length > 0 && (
        <div id={`tier-body-${slug}`}>
          <table
            data-testid={`tier-table-${slug}`}
            style={{ width: '100%', borderCollapse: 'collapse' }}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                <th style={ruleRowCellStyle}>Code</th>
                <th style={ruleRowCellStyle}>Description</th>
                <th style={ruleRowCellStyle}>Agencies</th>
                <th style={ruleRowCellStyle}>Severity</th>
                <th style={ruleRowCellStyle}>Category</th>
              </tr>
            </thead>
            <tbody>{rules.map((rule) => renderRow(rule))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
